import dayjs from 'dayjs';
import { flat, map, pipe, reverse, sortBy } from 'remeda';
import type {
  DebtorBalance,
  DebtorBalanceCreditNote,
  DebtorBalanceInvoice,
  DebtorBalancePayment,
} from 'shared/hooks/use-load-debtor-balance';

import type { TableRow } from './BalanceTable/hooks/use-balance-table-columns';

const n = (v: number | null | undefined) => v ?? 0;

export const getBalanceColor = (value: number) => {
  if (value > 0) return 'red.5';
  if (value < 0) return 'validation.6';
  return 'gray.10';
};

const normalizeInvoice = (inv: DebtorBalanceInvoice): TableRow => {
  const totalWithFees = n(inv.totalTvac) + n(inv.lateFees);
  const remainingBalanceWithFees = n(inv.remainingBalance) + n(inv.remainingLateFees);

  return {
    id: inv.id,
    type: 'invoice',
    reference: inv.reference ?? null,
    status: inv.status ?? null,
    issueDate: dayjs(inv.issueDate),
    dueDate: dayjs(inv.dueDate),
    debit: totalWithFees,
    credit: totalWithFees - remainingBalanceWithFees,
    balance: remainingBalanceWithFees,
    invoices: inv,
  };
};

const normalizeCreditNote = (cn: DebtorBalanceCreditNote): TableRow => {
  const total = n(cn.totalTvac);
  const remaining = n(cn.remainingBalance);

  return {
    id: cn.id,
    type: 'credit_note',
    reference: cn.reference ?? null,
    status: cn.status ?? null,
    issueDate: dayjs(cn.issueDate),
    dueDate: cn.dueDate ? dayjs(cn.dueDate) : null,
    debit: (total - remaining) * -1,
    credit: total * -1,
    balance: remaining,
    creditNotes: cn,
  };
};

const normalizePayment = (p: DebtorBalancePayment): TableRow => ({
  type: 'payment',
  reference: p.communication ?? null,
  status: null,
  issueDate: dayjs(p.issueDate),
  dueDate: null,
  debit: null,
  credit: n(p.remainingBalance),
  balance: n(p.remainingBalance * -1),
  paymentId: p.paymentId ?? null,
  paymentType: p.paymentType ?? null,
});

export const transformToTableRow = (data: DebtorBalance): Array<TableRow> =>
  pipe(
    [
      map(data.invoices, normalizeInvoice),
      map(data.creditNotes, normalizeCreditNote),
      map(data.payments, normalizePayment),
    ],
    flat(),
    sortBy(item => item.issueDate),
    reverse(),
  );
